'use client';

import {
    FILTER,
    FILTER_VAL,
    SHOP_BY_PREFIX,
    TRACK_TYPE_MULTI_ATTR,
} from '@/components/Analytics/constants';
import { PageTypes } from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { prependCategoryUrl } from '@/services/search/utils';
import { CURRENT_PAGE } from '@/src/components/SearchResultsFacets/constants';
import {
    BreadcrumbNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import { PageType } from '@/types/pages/search';
import { Box } from '@mui/material';
import { Link, List } from '@nxt-costco-com/forge-components';
import { ColorTextDefault } from '@nxt-costco-com/forge-design-tokens';
import {
    Analytics,
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import { BreadCrumbFilterView } from './breadcrumbs.view';
import { DEFAULT_ITEMS_TO_LIST } from './constants';
import {
    CHILD_CATEGORY_A11Y_TEXT,
    PARENT_CATEGORY_A11Y_TEXT,
} from './constants';

export function generateButtonId(facet: FacetNormalized): string {
    return `show_more_${facet.label.toLowerCase().split(' ').join('_')}`;
}

export function CategoryFilterView({
    categoryUrlPath,
    facet,
    resultsText,
    facetConfig,
    pageType,
    categoryId,
    breadcrumb,
    publishedCategories = [],
}: {
    categoryUrlPath: string | undefined;
    facet: FacetNormalized;
    resultsText: string;
    facetConfig: ContentStackEntryDataProps;
    pageType: PageType;
    breadcrumb?: BreadcrumbNormalized;
    categoryId?: string;
    publishedCategories?: string[];
}) {
    const { queryParams: params } = useQueryParams();
    const isCategoryPage = pageType === PageTypes.CATEGORY || !!categoryId;

    const showMoreText = getKeyValue('facet.showmoretext', facetConfig);

    // Build the query string once — intentionally exclude currentPage so that
    // clicking a category filter always resets pagination back to page 1.
    const paramsWithoutCurrentPage = (() => {
        const p = new URLSearchParams(params.toString());
        p.delete(CURRENT_PAGE);
        return p.toString();
    })();

    if (!facet.buckets) {
        return null;
    }

    return (
        <>
            <BreadCrumbFilterView
                showMoreText={showMoreText}
                facet={facet}
                breadcrumb={breadcrumb}
                categoryUrlPath={categoryUrlPath}
                publishedCategories={publishedCategories}
                parentCategoryName={getKeyValue(
                    PARENT_CATEGORY_A11Y_TEXT,
                    facetConfig,
                )}
                enabled={isCategoryPage}
                facetConfig={facetConfig}
            />

            <List
                variant="ExpandableList"
                showMoreText={showMoreText}
                ariaLabeledById={facet.facetKey}
                buttonId={generateButtonId(facet)}
                defaultItemsToExpose={DEFAULT_ITEMS_TO_LIST}
            >
                {facet.buckets.map(bucket => {
                    if (!bucket.val) {
                        return null;
                    }

                    const nesting = bucket.val?.toString()?.split('|') ?? [];
                    const s = nesting[nesting.length - 1] ?? '';

                    return (
                        <Analytics
                            key={bucket.val?.toString()}
                            analyticData={{
                                trackedData: [
                                    {
                                        key: FILTER,
                                        value: SHOP_BY_PREFIX + facet.label,
                                    },
                                    {
                                        key: FILTER_VAL,
                                        value: s ? s : '',
                                    },
                                ],
                                trackType: TRACK_TYPE_MULTI_ATTR,
                                doNotTrack: bucket.leads_to_fsa_chdi,
                            }}
                        >
                            <Box
                                key={bucket.val?.toString()}
                                sx={{
                                    paddingLeft: isCategoryPage
                                        ? '.625rem'
                                        : '',
                                    display: 'flex',
                                    alignItems: 'flext-start',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {/** Category links always route to the .html page with just a keyword */}
                                <Link
                                    automation-id={`${facet.facetKey}`}
                                    href={prependCategoryUrl(
                                        categoryUrlPath,
                                        bucket.meta ?? '',
                                        paramsWithoutCurrentPage,
                                        publishedCategories,
                                        false,
                                    )}
                                    underline="always"
                                    aria-label={`${s}, ${getKeyValue(CHILD_CATEGORY_A11Y_TEXT, facetConfig)} ${bucket.count?.toLocaleString()} ${resultsText}`}
                                    sx={{
                                        color: ColorTextDefault,
                                        textDecoration: 'underline',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                    }}
                                >
                                    <Box component={'span'}>{s}</Box>
                                    <Box
                                        component={'span'}
                                        id={`${facet.facetKey}-${bucket.val?.toString()}`}
                                        sx={{
                                            color: 'rgb(51, 51, 51)',
                                        }}
                                        aria-hidden={true}
                                    >
                                        ({bucket.count?.toLocaleString()})
                                    </Box>
                                </Link>
                            </Box>
                        </Analytics>
                    );
                })}
            </List>
        </>
    );
}
